import React, { createContext, useContext, useEffect, useState } from 'react'
import { useAdvancedEnabled } from '../useAdvancedEnabled'
import { advancedGet } from './client'
import { useAdvancedResource } from './useAdvancedResource'

export type AdvancedSettings = {
  careTeamChatExcludedRoles: string[]
  stalledAfterDays: number
}

export const DEFAULT_ADVANCED_SETTINGS: AdvancedSettings = {
  careTeamChatExcludedRoles: ['care-coordinator'],
  stalledAfterDays: 7,
}

type SettingsContext = {
  settings: AdvancedSettings | null
  updateSettings: (settings: AdvancedSettings) => void
}

const AdvancedSettingsContext = createContext<SettingsContext>({
  settings: null,
  updateSettings: () => undefined,
})

export const AdvancedSettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const enabled = useAdvancedEnabled()
  const resource = useAdvancedResource(
    (signal) => advancedGet<{ settings: AdvancedSettings }>('/settings', signal),
    [enabled],
    !enabled
  )
  const { retry } = resource
  const [saved, setSaved] = useState<AdvancedSettings | null>(null)

  useEffect(() => {
    if (resource.status === 'ready') setSaved(resource.data.settings)
    if (resource.status === 'error') setSaved(null)
  }, [resource])

  useEffect(() => {
    if (!enabled) return undefined
    const refresh = () => retry()
    const interval = window.setInterval(refresh, 60_000)
    window.addEventListener('focus', refresh)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', refresh)
    }
  }, [enabled, retry])

  return (
    <AdvancedSettingsContext.Provider value={{ settings: enabled ? saved : null, updateSettings: setSaved }}>
      {children}
    </AdvancedSettingsContext.Provider>
  )
}

export const useAdvancedSettings = () => useContext(AdvancedSettingsContext)
export const useStalledAfterDays = () =>
  useAdvancedSettings().settings?.stalledAfterDays ?? DEFAULT_ADVANCED_SETTINGS.stalledAfterDays

export const canonicalCareTeamRole = (role: string): string => (role === 'care-navigator' ? 'care-coordinator' : role)
